import { useMemo } from 'react';
import { DonutChart } from '@/components/charts/DonutChart';
import { palette } from '@/components/charts/chartTheme';
import { fmt } from '@/lib/format';
import type { ProductPerfRow } from '@/api/bi';

interface MixDonutProps {
  rows: ProductPerfRow[];
  /** How many real slices before everything else folds into "Others". */
  topN?: number;
  onSliceClick?: (id: number | null) => void;
}

/**
 * Revenue-mix donut for a single grouping dimension. Folds the long tail
 * into an "Others" slice so the chart stays legible, and renders a compact
 * legend with GMV + share next to it.
 */
export function MixDonut({ rows, topN = 6, onSliceClick }: MixDonutProps) {
  const { slices, legend } = useMemo(() => {
    const sorted = [...rows].sort((a, b) => b.gmv - a.gmv);
    const head = sorted.slice(0, topN);
    const tail = sorted.slice(topN);
    const tailGmv = tail.reduce((s, r) => s + r.gmv, 0);
    const total = sorted.reduce((s, r) => s + r.gmv, 0) || 1;

    const slicesOut = head.map((r) => ({ name: r.name, value: r.gmv, id: r.id as number | null }));
    if (tailGmv > 0) slicesOut.push({ name: 'Others', value: tailGmv, id: null });

    const legendOut = slicesOut.map((s, i) => ({
      ...s,
      share: s.value / total,
      color: palette.series[i % palette.series.length](),
    }));
    return { slices: slicesOut.map(({ name, value }) => ({ name, value })), legend: legendOut };
  }, [rows, topN]);

  if (rows.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No data in range.</p>;
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="sm:w-1/2">
        <DonutChart
          data={slices}
          formatValue={(v) => fmt.npr(v)}
          onSliceClick={
            onSliceClick
              ? (slice) => {
                  const match = legend.find((l) => l.name === slice.name);
                  onSliceClick(match?.id ?? null);
                }
              : undefined
          }
        />
      </div>
      <ul className="flex-1 space-y-1.5">
        {legend.map((l) => (
          <li key={l.name} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: l.color }} aria-hidden />
            <span className="min-w-0 flex-1 truncate text-foreground">{l.name}</span>
            <span className="kpi-num text-muted-foreground">{fmt.pct(l.share)}</span>
            <span className="kpi-num w-20 text-right font-medium">{fmt.npr(l.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
